/**
 * @file ページからヘッダーへ「いま見ている家族」を渡す仕組み。
 *
 * @what: ページが `useHeaderFamily({ label, role, switchable })` を呼ぶと、ヘッダー右のメニューが
 *   その家族名を出し、役割と「家族を変える」を中に畳む。ページを離れると消える。
 * @why: 家族名と役割は毎ページの本文に出すほどの情報ではなく、メニューに入っていればよい
 *   (代表 2026-08-23)。家族の一覧はページ側が既に読んでいるので、ヘッダーでもう一度
 *   取りに行かず、ページから値だけ渡す。
 * @failure: Layout の外 (テスト等) では setter が何もしない既定値になる。
 */

import { createContext, useContext, useEffect } from "react";

export interface HeaderFamily {
  /** 家族の表示名。 */
  label: string;
  /** 自分の役割の表示名 (親 / 祖父母 など)。 */
  role: string;
  /** 所属が複数あり「家族を変える」を出すか。 */
  switchable: boolean;
}

export interface HeaderContextValue {
  family: HeaderFamily | null;
  setFamily: (family: HeaderFamily | null) => void;
}

export const HeaderContext = createContext<HeaderContextValue>({
  family: null,
  setFamily: () => {},
});

/** いま見ている家族をヘッダーに渡す (null で消す)。unmount で必ず消す。 */
export function useHeaderFamily(family: HeaderFamily | null): void {
  const { setFamily } = useContext(HeaderContext);
  const label = family?.label ?? null;
  const role = family?.role ?? null;
  const switchable = family?.switchable ?? false;
  useEffect(() => {
    setFamily(label === null || role === null ? null : { label, role, switchable });
    return () => setFamily(null);
  }, [setFamily, label, role, switchable]);
}
